import dayjs from "dayjs"
import React, { useEffect } from "react"

import DatePicker from "../../components/shared/DatePicker"
import { Label, Select, TimePicker } from "../../components/shared/Inputs"
import LoadingSpinner from "../../components/shared/LoadingSpinner"
import { resolveIanaTimeZone } from "../../utils/timeZoneUtils"
import { useBookableEventTypes } from "../BookableEventTypes/BookableEventTypesContext"

import { useBookableEvents } from "./BookableEventsContext"

const bookableEventTz = (timeZone) => resolveIanaTimeZone(timeZone)

/** Matches BookableEventForm.calculateInitialDates — end instant from start + event type length */
function endsAtFromStartsAtAndEventType(startsAt, eventType) {
  if (!startsAt || !eventType?.duration) return null
  const start = dayjs(startsAt)
  if (eventType.durationUnit === "day") {
    return start.add(eventType.duration - 1, "day").toISOString()
  }
  if (eventType.durationUnit === "hour") {
    return start.add(eventType.duration, "hour").toISOString()
  }
  return start.add(eventType.duration, "minute").toISOString()
}

const BookableEventFormDetails = () => {
  const { bookableEvent, setBookableEvent, fetching, groupProgramEventsEnabled } = useBookableEvents()
  const { bookableEventTypes, fetching: fetchingEventTypes } = useBookableEventTypes()

  // When passing a bookable event type id in the url, set the bookable event type id
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const bookableEventTypeId = urlParams.get("bookableEventTypeId")
    if (bookableEventTypeId) {
      setBookableEvent((prev) => ({ ...prev, bookableEventTypeId }))
      urlParams.delete("bookableEventTypeId")
      window.history.replaceState({}, "", window.location.pathname)
    }
  }, [])

  // Calculate end date for multi-day events
  const calculateEndDate = (startDate, durationDays) =>
    dayjs(startDate)
      .add(durationDays - 1, "day")
      .format("MM/DD/YYYY")

  // Handle date selection
  const handleDateChange = (date) => {
    setBookableEvent((event) => {
      // Preserve the time component when changing date
      const newStartsAt = dayjs(date)
        .hour(dayjs(event.startsAt).tz(bookableEventTz(event.timeZone)).hour())
        .minute(dayjs(event.startsAt).tz(bookableEventTz(event.timeZone)).minute())
        .toDate()

      let newEndsAt
      if (isMultiDay && selectedEventType?.duration) {
        // For multi-day events, adjust end date based on start date + duration
        newEndsAt = dayjs(newStartsAt)
          .add(selectedEventType.duration - 1, "day")
          .hour(dayjs(event.endsAt).tz(bookableEventTz(event.timeZone)).hour())
          .minute(dayjs(event.endsAt).tz(bookableEventTz(event.timeZone)).minute())
          .toDate()
      } else {
        // Single-day: length comes from event type (prior endsAt may be the default 60m window)
        let durationMinutes
        if (selectedEventType?.durationUnit === "hour") {
          durationMinutes = (selectedEventType?.duration || 1) * 60
        } else if (selectedEventType) {
          durationMinutes = selectedEventType.duration || 60
        } else {
          durationMinutes = dayjs(event.endsAt).diff(dayjs(event.startsAt), "minute")
        }
        newEndsAt = dayjs(newStartsAt).add(durationMinutes, "minute").toDate()
      }

      return { ...event, startsAt: newStartsAt, endsAt: newEndsAt }
    })
  }

  // Handle time selection
  const handleTimeChange = (timeString, isEndTime = false) => {
    const [hour, minute] = timeString.split(":")

    if (isEndTime) {
      setBookableEvent((event) => ({
        ...event,
        endsAt: dayjs(event.endsAt).set("hour", hour).set("minute", minute).toDate()
      }))
    } else {
      setBookableEvent((event) => {
        const newStartsAt = dayjs(event.startsAt).set("hour", hour).set("minute", minute).toDate()
        let newEndsAt = event.endsAt

        if (!isMultiDay) {
          // For single-day events, maintain the same duration
          const durationMinutes =
            selectedEventType?.durationUnit === "hour"
              ? (selectedEventType?.duration || 1) * 60
              : selectedEventType?.duration || 60

          newEndsAt = dayjs(newStartsAt).add(durationMinutes, "minute").toDate()
        }

        return { ...event, startsAt: newStartsAt, endsAt: newEndsAt }
      })
    }
  }

  // Handle timezone change
  const handleTimezoneChange = (newTimezone) => {
    setBookableEvent((event) => {
      const startsAt = dayjs(event.startsAt)
        .tz(newTimezone)
        .set("hour", dayjs(event.startsAt).tz(bookableEventTz(event.timeZone)).get("hour"))
        .set("minute", dayjs(event.startsAt).tz(bookableEventTz(event.timeZone)).get("minute"))
        .toISOString()

      const endsAt = dayjs(event.endsAt)
        .tz(newTimezone)
        .set("hour", dayjs(event.endsAt).tz(bookableEventTz(event.timeZone)).get("hour"))
        .set("minute", dayjs(event.endsAt).tz(bookableEventTz(event.timeZone)).get("minute"))
        .toISOString()

      return {
        ...event,
        timeZone: newTimezone,
        startsAt,
        endsAt
      }
    })
  }

  const selectedEventType = bookableEventTypes?.find(
    (et) => et.id.toString() === bookableEvent.bookableEventTypeId?.toString()
  )
  const isLoading = fetching || fetchingEventTypes
  const isMultiDay = selectedEventType?.durationUnit === "day"

  // New events only expose start time in the UI; endsAt must follow the type duration (not the calendar default +60m).
  useEffect(() => {
    if (bookableEvent?.id || !selectedEventType || !bookableEvent?.startsAt || !bookableEvent?.timeZone) return
    if (selectedEventType.durationUnit === "day") return

    const nextEnd = endsAtFromStartsAtAndEventType(bookableEvent.startsAt, selectedEventType)
    if (!nextEnd) return

    if (dayjs(bookableEvent.endsAt).valueOf() !== dayjs(nextEnd).valueOf()) {
      setBookableEvent((prev) => ({ ...prev, endsAt: nextEnd }))
    }
  }, [
    bookableEvent?.id,
    bookableEvent?.startsAt,
    bookableEvent?.timeZone,
    bookableEvent?.endsAt,
    bookableEvent?.bookableEventTypeId,
    selectedEventType?.id,
    selectedEventType?.duration,
    selectedEventType?.durationUnit,
    setBookableEvent
  ])

  // Calculate end date for display in UI (for multi-day events)
  const endDate =
    isMultiDay && bookableEvent.startsAt && selectedEventType?.duration
      ? calculateEndDate(bookableEvent.startsAt, selectedEventType.duration)
      : null

  if (isLoading) {
    return <LoadingSpinner className="py-12" spinnerClassName="text-teal h-12 w-12" />
  }

  return (
    <>
      {selectedEventType && bookableEvent.timeZone && (
        <>
          {/* Date and Time Section */}
          {isMultiDay ? (
            // Multi-day layout
            <>
              <div className="mb-4 w-full">
                <Label>Start date</Label>
                <div className="flex items-center">
                  <DatePicker
                    className="w-full"
                    selected={new Date(dayjs(bookableEvent.startsAt).tz(bookableEventTz(bookableEvent.timeZone)))}
                    onChange={handleDateChange}
                  />
                  {endDate && <div className="ml-4">Ends on {endDate}</div>}
                </div>
              </div>

              <div className="flex flex-row justify-between gap-4">
                <div className="w-full md:w-1/2">
                  <Label htmlFor="bookable-event-start-time">Start time</Label>
                  <TimePicker
                    id="bookable-event-start-time"
                    value={dayjs(bookableEvent.startsAt).tz(bookableEventTz(bookableEvent.timeZone)).format("HH:mm")}
                    valueFormat="HH:mm"
                    showFifteenIncrements={true}
                    onChange={(e) => handleTimeChange(e.target.value)}
                  />
                  <div className="mt-1 text-sm text-gray-dark">Start time for first day.</div>
                </div>

                <div className="w-full md:w-1/2">
                  <Label htmlFor="bookable-event-end-time">End time</Label>
                  <TimePicker
                    id="bookable-event-end-time"
                    value={dayjs(bookableEvent.endsAt).tz(bookableEventTz(bookableEvent.timeZone)).format("HH:mm")}
                    valueFormat="HH:mm"
                    showFifteenIncrements={true}
                    onChange={(e) => handleTimeChange(e.target.value, true)}
                  />
                  <div className="mt-1 text-sm text-gray-dark">End time for last day.</div>
                </div>
              </div>
            </>
          ) : (
            // Single-day layout - Date and Time side by side
            <div className="mb-4 flex flex-row gap-4 md:flex-col">
              <div className="w-full min_md:w-1/2">
                <Label>Start date</Label>
                <DatePicker
                  className="w-full"
                  selected={new Date(dayjs(bookableEvent.startsAt).tz(bookableEventTz(bookableEvent.timeZone)))}
                  onChange={handleDateChange}
                />
              </div>
              <div className="w-full min_md:w-1/2">
                <Label htmlFor="bookable-event-start-time">Start time</Label>
                <TimePicker
                  id="bookable-event-start-time"
                  value={dayjs(bookableEvent.startsAt).tz(bookableEventTz(bookableEvent.timeZone)).format("HH:mm")}
                  valueFormat="HH:mm"
                  showFifteenIncrements={true}
                  onChange={(e) => handleTimeChange(e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="mt-4">
            <Label htmlFor="time-zone">Time zone</Label>
            <Select
              value={bookableEventTz(bookableEvent.timeZone)}
              onChange={(e) => handleTimezoneChange(e.target.value)}
              id="time-zone">
              {Intl.supportedValuesOf("timeZone").map((tz) => (
                <option key={tz} value={tz}>
                  {tz}
                </option>
              ))}
            </Select>
          </div>

          <div className="mt-4">
            <Label>Recurring</Label>
            <Select
              value={bookableEvent.recurrencePattern || ""}
              disabled={!!bookableEvent.id}
              onChange={(e) =>
                setBookableEvent((event) => ({
                  ...event,
                  recurrencePattern: e.target.value || null,
                  isProgram: e.target.value ? event.isProgram : false
                }))
              }>
              <option value="">Does not repeat</option>
              <option value="FREQ=WEEKLY;INTERVAL=1;COUNT=4">Weekly — 4 sessions</option>
              <option value="FREQ=WEEKLY;INTERVAL=1;COUNT=6">Weekly — 6 sessions</option>
              <option value="FREQ=WEEKLY;INTERVAL=1;COUNT=8">Weekly — 8 sessions</option>
              <option value="FREQ=WEEKLY;INTERVAL=2;COUNT=4">Every other week — 4 sessions</option>
              <option value="FREQ=WEEKLY;INTERVAL=2;COUNT=6">Every other week — 6 sessions</option>
              <option value="FREQ=WEEKLY;INTERVAL=2;COUNT=8">Every other week — 8 sessions</option>
            </Select>
            {!bookableEvent.id && bookableEvent.recurrencePattern && (
              <p className="mt-1 text-sm text-gray-dark">
                Creates a series of group events on the same day and time. Edit individual dates after saving.
              </p>
            )}
          </div>

          {!bookableEvent.id && bookableEvent.recurrencePattern && groupProgramEventsEnabled && (
            <>
              <div className="mt-4">
                <Label>Program type</Label>
                <Select
                  value={bookableEvent.isProgram ? "program" : "series"}
                  onChange={(e) =>
                    setBookableEvent((event) => ({
                      ...event,
                      isProgram: e.target.value === "program"
                    }))
                  }>
                  <option value="series">Recurring events (register per session)</option>
                  <option value="program">Group program (register once, pay once)</option>
                </Select>
              </div>

              {bookableEvent.isProgram && (
                <div className="mt-4">
                  <Label>Payment timing</Label>
                  <Select
                    value={bookableEvent.paymentTiming || "upfront"}
                    onChange={(e) => setBookableEvent((event) => ({ ...event, paymentTiming: e.target.value }))}>
                    <option value="upfront">Charge upfront at registration</option>
                    <option value="first_session">Capture when first session ends</option>
                  </Select>
                </div>
              )}
            </>
          )}
        </>
      )}
    </>
  )
}

export default BookableEventFormDetails
